<template>
  <div class="card">
    <div class="card-header">
      {{ isEditing ? $t("Edit Problem Category") : $t("Create Problem Category") }}
    </div>
    <div class="card-body">
      <b-form @submit.prevent="handleSubmit">
        <b-row>
          <b-col md="6">
            <!-- Company Selection -->
            <b-form-group
              label="Company"
              :state="getFieldState('company_id')"
              invalid-feedback="Please select a company"
            >
              <b-form-select
                v-model="form.company_id"
                :options="formattedCompanies"
                :state="getFieldState('company_id')"
                required
              >
                <option value="">{{ $t("Select company") }}</option>
              </b-form-select>
            </b-form-group>
          </b-col>

          <b-col md="6">
            <!-- Problem Department Selection -->
            <b-form-group
              label="Problem Department"
              :state="getFieldState('problem_department_id')"
              invalid-feedback="Please select a department"
            >
              <b-form-select
                v-model="form.problem_department_id"
                :options="departmentOptions"
                :state="getFieldState('problem_department_id')"
                required
              ></b-form-select>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Name Translations -->
        <b-row>
          <b-col md="4" sm="6" class="col-xs-12" v-for="language in languages" :key="language.code">
            <b-form-group
              :label="`${$t('Name')} (${language.name})`"
              :state="getFieldState(`name.${language.code}`)"
              :invalid-feedback="getErrorMessage(`name.${language.code}`)"
            >
              <b-form-input
                v-model="form.name[language.code]"
                :state="getFieldState(`name.${language.code}`)"
              ></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Submit Button -->
        <b-button :disabled="loading" variant="primary" type="submit">
          <span v-if="loading" class="spinner-border spinner-border-sm" role="status"></span>
          {{ isEditing ? $t("Save") : $t("Create") }}
        </b-button>
      </b-form>
    </div>
  </div>
</template>

<script>
import axios from "axios";
export default {
  name: "ProblemCategoryForm",
  props: {
    problemCategory: {
      type: Object,
      default: () => ({})
    },
    companies: {
      type: Array,
      required: true
    },
    departments: {
      type: Array,
      required: true
    },
    languages: {
      type: Array,
      required: true
    },
    formAction: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      form: {
        company_id: this.problemCategory.company_id || "",
        problem_department_id: this.problemCategory.problem_department_id || "",
        name: this.languages.reduce((acc, lang) => {
          acc[lang.code] = this.problemCategory.name?.[lang.code] || "";
          return acc;
        }, {})
      },
      errors: {},
      loading: false
    };
  },
  computed: {
    isEditing() {
      return !!this.problemCategory.id;
    },
    formattedCompanies() {
      return this.companies.map(company => ({
        value: company.id,
        text: company.name
      }));
    },
    filteredDepartments() {
      return this.departments.filter(
        department => department.company_id === this.form.company_id
      );
    },
    departmentOptions() {
      const defaultOption = { value: "", text: this.$t("Select department") };
      const filtered = this.departments
        .filter(department => department.company_id === this.form.company_id)
        .map(department => ({
          value: department.id,
          text: department.name
        }));
      return [defaultOption, ...filtered];
    }
  },
  watch: {
    "form.company_id": {
      immediate: true,
      handler() {
        this.form.problem_department_id = ""; // Réinitialise le département
      }
    }
  },
  mounted() {
    this.initializeDepartment();
  },
  methods: {
    initializeDepartment() {
      // Si le département actuel est valide, ne rien changer
      if (
        this.filteredDepartments.some(
          department => department.id === this.form.problem_department_id
        )
      ) {
        return;
      }

      // Sinon, sélectionner le premier département disponible ou rien
      const firstDepartment = this.filteredDepartments[0];
      this.form.problem_department_id = firstDepartment
        ? firstDepartment.id
        : "";
    },
    getFieldState(field) {
      return this.errors[field] === undefined ? null : false;
    },
    getErrorMessage(field) {
      return this.errors[field]?.[0] || "This field is invalid";
    },
    async handleSubmit() {
      this.loading = true;
      this.errors = {};

      try {
        const payload = { ...this.form };

        const method = this.isEditing ? "put" : "post";

        await axios[method](this.formAction, payload);

        this.$bvToast.toast(
          this.isEditing
            ? "Problem category updated successfully!"
            : "Problem category created successfully!",
          {
            title: "Success",
            variant: "success",
            solid: true
          }
        );

        setTimeout(() => {
          window.location.href = "/settings/problem-categories";
        }, 1000);
      } catch (error) {
        if (error.response && error.response.data.errors) {
          this.errors = error.response.data.errors;
        } else {
          this.$bvToast.toast("An error occurred. Please try again.", {
            title: "Error",
            variant: "danger",
            solid: true
          });
        }
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>
